import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../UserContext';
import { updateUser } from '../../services/apiService';
import { searchAddress } from '../../services/apiService';
import { changePassword } from '../../services/apiService';
import { countryDialingInfo } from '../../components/PhonePrefixes';

function UserProfile() {
  const { user, setUser } = useContext(UserContext);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [fullPhoneNumber/*, setFullPhoneNumber*/] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const storedProfile = localStorage.getItem('userProfile');
    if (storedProfile) {
      setUser(JSON.parse(storedProfile));
    }
  }, [setUser]);

  useEffect(() => {
  const fetchSuggestions = async () => {
    if (user.address?.length > 3 && user.country) {
      try {
        const res = await searchAddress(user.address, user.country);
        setAddressSuggestions(res);
      } catch (err) {
        console.error("Failed to fetch address suggestions:", err);
        setAddressSuggestions([]);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

    fetchSuggestions();
  }, [user.address, user.country]);  

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'country') {
      setUser({
        ...user,
        country: value,
        address: '',
        phoneNumber: ''
      });
    } else {
      setUser({
        ...user,
        [name]: value
      });
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUser({
          ...user,
          profilePicture: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!user.email.trim()) {
      newErrors.email = t('emailRequired');
    }
    if (!user.phoneNumber?.trim()) {
      newErrors.phoneNumber = t('phoneNumberRequired');
    } else if (!/^[0-9()]{3,20}$/.test(user.phoneNumber)) {
      newErrors.phoneNumber = t('phoneNumberInvalid');
    }
    if (!user.address?.trim()) {
      newErrors.address = t('addressRequired');
    }
    if (!user.country) {
      newErrors.country = t('countryRequired');
    }    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
  if (changingPassword) {
    if (newPassword !== confirmPassword) {
      setPasswordError(t('passwordsDoNotMatch'));
      return;
    }

  try {
    await changePassword(user.id, oldPassword, newPassword);
    } catch (err) {
      console.error('Password update failed:', err);
      setPasswordError(t('incorrectOldPassword'));
      return;
    }
  }
    
  try {
    const updatedUser = await updateUser(user.id, user);
    setUser(updatedUser);
    localStorage.setItem('userProfile', JSON.stringify(updatedUser));
    alert(t('profileSaved'));
    setChangingPassword(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    } catch (err) {
      console.error("Error updating profile:", err);
      alert("Error saving profile. Please try again.");
    }
  };
  
  return (
    <div>
      <h1>{t('editProfile')}</h1>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          {user.profilePicture ? (
            <img 
              src={user.profilePicture} 
              alt="Profile" 
              style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }} 
            />
          ) : (
            <div style={{
              width: '100px', 
              height: '100px', 
              border: '1px solid #ccc', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              {t('noImage')}
            </div>
          )}
        </div>
        <div>
          <label>{t('profilePicture')}</label>
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleProfilePictureChange}
          />
        </div>
        <div>
          <label>{t('firstName')}</label>
          <input 
            type="text" 
            name="firstName" 
            value={user.firstName} 
            onChange={handleChange}
            readOnly
            title="This field cannot be changed"
          />
        </div>
        <div>
          <label>{t('lastName')}</label>
          <input 
            type="text" 
            name="lastName" 
            value={user.lastName} 
            onChange={handleChange}
            readOnly
            title="This field cannot be changed"
          />
        </div>
        <div>
          <label>{t('companyName')}</label>
          <input 
            type="text" 
            name="companyName" 
            value={user.companyName} 
            onChange={handleChange}
            readOnly
            title="This field cannot be changed"
          />
        </div>
        <div>
          <label>{t('email')}</label>
          <input 
            type="email" 
            name="email" 
            value={user.email} 
            onChange={handleChange}
          />
          {errors.email && <span style={{ color: 'red' }}>{errors.email}</span>}
          </div>
            <label>{t('country')}</label>
              <select 
                style={{ fontFamily: 'Figtree', textAlign: 'center' }}
                name="country"
                value={user.country || ''}
                onChange={handleChange}
              >
                <option value="">-- {t('selectCountry')} --</option>
                <option value="gb">United Kingdom</option>
                <option value="ad">Andorra</option>
                <option value="ae">United Arab Emirates</option>
                <option value="af">Afghanistan</option>
                <option value="ag">Antigua and Barbuda</option>
                <option value="ai">Anguilla</option>
                <option value="al">Albania</option>
                <option value="am">Armenia</option>
                <option value="ao">Angola</option>
                <option value="aq">Antarctica</option>
                <option value="ar">Argentina</option>
                <option value="as">American Samoa</option>
                <option value="at">Austria</option>
                <option value="au">Australia</option>
                <option value="aw">Aruba</option>
                <option value="ax">Aland Islands</option>
                <option value="az">Azerbaijan</option>
                <option value="ba">Bosnia and Herzegovina</option>
                <option value="bb">Barbados</option>
                <option value="bd">Bangladesh</option>
                <option value="be">Belgium</option>
                <option value="bf">Burkina Faso</option>
                <option value="bg">Bulgaria</option>
                <option value="bh">Bahrain</option>
                <option value="bi">Burundi</option>
                <option value="bj">Benin</option>
                <option value="bl">Saint-Barthélemy</option>
                <option value="bm">Bermuda</option>
                <option value="bn">Brunei Darussalam</option>
                <option value="bo">Bolivia</option>
                <option value="bq">Caribbean Netherlands</option>
                <option value="br">Brazil</option>
                <option value="bs">Bahamas</option>
                <option value="bt">Bhutan</option>
                <option value="bv">Bouvet Island</option>
                <option value="bw">Botswana</option>
                <option value="by">Belarus</option>
                <option value="bz">Belize</option>
                <option value="ca">Canada</option>
                <option value="cc">Cocos (Keeling) Islands</option>
                <option value="cd">Democratic Republic of the Congo</option>
                <option value="cf">Central African Republic</option>
                <option value="cg">Republic of the Congo</option>
                <option value="ch">Switzerland</option>
                <option value="ci">Côte d'Ivoire (Ivory Coast) </option>
                <option value="ck">Cook Islands</option>
                <option value="cl">Chile</option>
                <option value="cm">Cameroon</option>
                <option value="cn">China</option>
                <option value="co">Colombia</option>
                <option value="cr">Costa Rica</option>
                <option value="cu">Cuba</option>
                <option value="cv">Cabo Verde</option>
                <option value="cw">Curaçao</option>
                <option value="cx">Christmas Island</option>
                <option value="cy">Cyprus</option>
                <option value="cz">Czech Republic</option>
                <option value="de">Germany</option>
                <option value="dj">Djibouti</option>
                <option value="dk">Denmark</option>
                <option value="dm">Dominica</option>
                <option value="do">Dominican Republic</option>
                <option value="dz">Algeria</option>
                <option value="ec">Ecuador</option>
                <option value="ee">Estonia</option>
                <option value="eg">Egypt</option>
                <option value="eh">Western Sahara</option>
                <option value="er">Eritrea</option>
                <option value="es">Spain</option>
                <option value="et">Ethiopia</option>
                <option value="fi">Finland</option>
                <option value="fj">Fiji</option>
                <option value="fk">Falkland Islands</option>
                <option value="fm">Micronesia (Federated States of) </option>
                <option value="fo">Faroe Islands</option>
                <option value="fr">France</option>
                <option value="ga">Gabon</option>
                <option value="gd">Grenada</option>
                <option value="ge">Georgia</option>
                <option value="gf">French Guiana</option>
                <option value="gg">Guernsey</option>
                <option value="gh">Ghana</option>
                <option value="gi">Gibraltar</option>
                <option value="gl">Greenland</option>
                <option value="gm">The Gambia</option>
                <option value="gn">Guinea</option>
                <option value="gp">Guadeloupe</option>
                <option value="gq">Equatorial Guinea</option>
                <option value="gr">Greece</option>
                <option value="gs">South Georgia and the South Sandwich Islands</option>
                <option value="gt">Guatemala</option>
                <option value="gu">Guam</option>
                <option value="gw">Guinea Bissau</option>
                <option value="gy">Guyana</option>
                <option value="hk">Hong Kong (SAR of China)</option>
                <option value="hm">Heard Island and McDonald Islands</option>
                <option value="hn">Honduras</option>
                <option value="hr">Croatia</option>
                <option value="ht">Haiti</option>
                <option value="hu">Hungary</option>
                <option value="id">Indonesia</option>
                <option value="ie">Ireland</option>
                <option value="il">Israel</option>
                <option value="im">Isle of Man</option>
                <option value="in">India</option>
                <option value="io">British Indian Ocean Territory</option>
                <option value="iq">Iraq</option>
                <option value="ir">Iran</option>
                <option value="is">Iceland</option>
                <option value="it">Italy</option>
                <option value="je">Jersey</option>
                <option value="jm">Jamaica</option>
                <option value="jo">Jordan</option>
                <option value="jp">Japan</option>
                <option value="ke">Kenya</option>
                <option value="kg">Kyrgyzstan</option>
                <option value="kh">Cambodia</option>
                <option value="ki">Kiribati</option>
                <option value="km">Comoros</option>
                <option value="kn">Saint Kitts and Nevis</option>
                <option value="kp">North Korea</option>
                <option value="kr">South Korea</option>
                <option value="kw">Kuwait</option>
                <option value="ky">Cayman Islands</option>
                <option value="kz">Kazakhstan</option>
                <option value="la">Laos</option>
                <option value="lb">Lebanon</option>
                <option value="lc">Saint Lucia</option>
                <option value="li">Liechtenstein</option>
                <option value="lk">Sri Lanka</option>
                <option value="lr">Liberia</option>
                <option value="ls">Lesotho</option>
                <option value="lt">Lithuania</option>
                <option value="lu">Luxembourg</option>
                <option value="lv">Latvia</option>
                <option value="ly">Libya</option>
                <option value="ma">Morocco</option>
                <option value="mc">Monaco</option>
                <option value="md">Moldova</option>
                <option value="me">Montenegro</option>
                <option value="mf">Saint Martin (French part) </option>
                <option value="mg">Madagascar</option>
                <option value="mh">Marshall Islands</option>
                <option value="mk">North Macedonia</option>
                <option value="ml">Mali</option>
                <option value="mm">Myanmar</option>
                <option value="mn">Mongolia</option>
                <option value="mo">Macao (SAR of China)</option>
                <option value="mp">Northern Mariana Islands</option>
                <option value="mq">Martinique</option>
                <option value="mr">Mauritania</option>
                <option value="ms">Montserrat</option>
                <option value="mt">Malta</option>
                <option value="mu">Mauritius</option>
                <option value="mv">Maldives</option>
                <option value="mw">Malawi</option>
                <option value="mx">Mexico</option>
                <option value="my">Malaysia</option>
                <option value="mz">Mozambique</option>
                <option value="na">Namibia</option>
                <option value="nc">New Caledonia</option>
                <option value="ne">Niger</option>
                <option value="nf">Norfolk Island</option>
                <option value="ng">Nigeria</option>
                <option value="ni">Nicaragua</option>
                <option value="nl">Netherlands</option>
                <option value="no">Norway</option>
                <option value="np">Nepal</option>
                <option value="nr">Nauru</option>
                <option value="nu">Niue</option>
                <option value="nz">New Zealand</option>
                <option value="om">Oman</option>
                <option value="pa">Panama</option>
                <option value="pe">Peru</option>
                <option value="pf">French Polynesia</option>
                <option value="pg">Papua New Guinea</option>
                <option value="ph">Philippines</option>
                <option value="pk">Pakistan</option>
                <option value="pl">Poland</option>
                <option value="pm">Saint Pierre and Miquelon</option>
                <option value="pn">Pitcairn</option>
                <option value="pr">Puerto Rico</option>
                <option value="ps">Palestinian Territory</option>
                <option value="pt">Portugal</option>
                <option value="pw">Palau</option>
                <option value="py">Paraguay</option>
                <option value="qa">Qatar</option>
                <option value="re">Reunion</option>
                <option value="ro">Romania</option>
                <option value="rs">Serbia</option>
                <option value="ru">Russia</option>
                <option value="rw">Rwanda</option>
                <option value="sa">Saudi Arabia</option>
                <option value="sb">Solomon Islands</option>
                <option value="sc">Seychelles</option>
                <option value="sd">Sudan</option>
                <option value="se">Sweden</option>
                <option value="sg">Singapore</option>
                <option value="sh">Saint Helena</option>
                <option value="si">Slovenia</option>
                <option value="sj">Svalbard and Jan Mayen</option>
                <option value="sk">Slovakia</option>
                <option value="sl">Sierra Leone</option>
                <option value="sm">San Marino</option>
                <option value="sn">Sénégal</option>
                <option value="so">Somalia</option>
                <option value="sr">Suriname</option>
                <option value="st">São Tomé and Príncipe</option>
                <option value="ss">South Sudan</option>
                <option value="sv">El Salvador</option>
                <option value="sx">Saint Martin (Dutch part)</option>
                <option value="sy">Syria</option>
                <option value="sz">eSwatini</option>
                <option value="tc">Turks and Caicos Islands</option>
                <option value="td">Chad</option>
                <option value="tf">French Southern and Antarctic Lands</option>
                <option value="tg">Togo</option>
                <option value="th">Thailand</option>
                <option value="tj">Tajikistan</option>
                <option value="tk">Tokelau</option>
                <option value="tl">Timor-Leste</option>
                <option value="tm">Turkmenistan</option>
                <option value="tn">Tunisia</option>
                <option value="to">Tonga</option>
                <option value="tr">Türkiye</option>
                <option value="tt">Trinidad and Tobago</option>
                <option value="tv">Tuvalu</option>
                <option value="tw">Taiwan</option>
                <option value="tz">Tanzania</option>
                <option value="ua">Ukraine</option>
                <option value="ug">Uganda</option>
                <option value="um">United States Minor Outlying Islands</option>
                <option value="us">United States of America</option>
                <option value="uy">Uruguay</option>
                <option value="uz">Uzbekistan</option>
                <option value="va">City of the Vatican</option>
                <option value="vc">Saint Vincent and the Grenadines</option>
                <option value="ve">Venezuela</option>
                <option value="vg">British Virgin Islands</option>
                <option value="vi">United States Virgin Islands</option>
                <option value="vn">Vietnam</option>
                <option value="vu">Vanuatu</option>
                <option value="wf">Wallis and Futuna</option>
                <option value="ws">Samoa</option>
                <option value="xk">Kosovo</option>
                <option value="ye">Yemen</option>
                <option value="yt">Mayotte</option>
                <option value="za">South Africa</option>
                <option value="zm">Zambia</option>
                <option value="zw">Zimbabwe</option>
              </select>
            {errors.country && <span style={{ color: 'red' }}> {errors.country}</span>}
        <div>
          <label>{t('address')}</label>
          <input 
            type="text" 
            name="address" 
            value={user.address || ''} 
            onChange={handleChange}
            disabled={!user.country}
          />
          {errors.address && <span style={{ color: 'red' }}>{errors.address}</span>}
          {addressSuggestions.length > 0 && (
            <ul style={{ border: '1px solid #ccc', padding: '5px', listStyle: 'none', maxHeight: '100px', overflowY: 'auto' }}>
              {addressSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  style={{ cursor: 'pointer', padding: '3px 5px' }}
                  onClick={() => {
                    setUser({ ...user, address: suggestion.display_name });
                    setAddressSuggestions([]);
                  }}
                >
                  {suggestion.display_name}
                </li>
              ))}
            </ul>
          )}
        </div>            
              <label>{t('phone')}: </label>
              <input type="tel"
              name="phoneNumber"
              value={user.phoneNumber || ''}
              onChange={handleChange}
              maxLength={20}
              minLength={3}
              required
              disabled={!user.country}
              placeholder={
                user.country
                  ? `${countryDialingInfo[user.country]?.prefix || ''} (${t('enterPhone')})`
                  : t('selectCountryFirst')
              }
              />
              {errors.phoneNumber && <span style={{ color: 'red' }}>{errors.phoneNumber}</span>}
              {fullPhoneNumber && (
                <div className="form-group">
                  <p><strong>{t('formattedPhone')}:</strong> {fullPhoneNumber}</p>
                </div>
              )}
        <div>
          <label>{t('sex')}</label>
          <select name="sex" title="This field cannot be changed" value={user.sex || ''} disabled>
            <option value="">{t('selectSex')}</option>
            <option value="male">{t('male')}</option>
            <option value="female">{t('female')}</option>
          </select>
        </div>
        <div>
          <label>{t('dob')}</label>
          <input 
            type="date" 
            name="dateOfBirth" 
            value={user.dateOfBirth || ''} 
            onChange={handleChange}
            min="1957-01-01"
            max="2001-12-12"
            readOnly
            title="This field cannot be changed"
          />
        </div>
        <div>
          <label>{t('speciality')}</label>
          <select name="speciality" title="This field cannot be changed" value={user.speciality || ''} disabled>
            <option value="">{t('enterSpeciality')}</option>
            <option value="neurologicalDisorder">{t('neurologicalDisorder')}</option>
            <option value="geriatrics">{t('geriatrics')}</option>
            <option value="cardiovascularPulmonaryPhysiotherapy">{t('cardiovascularPulmonaryPhysiotherapy')}</option>
            <option value="pediatricPhysiotherapy">{t('pediatricPhysiotherapy')}</option>
            <option value="musculoskeletalPhysiotherapy">{t('musculoskeletalPhysiotherapy')}</option>
            <option value="sportsPhysiotherapy">{t('sportsPhysiotherapy')}</option>
            <option value="cardiovascularDisease">{t('cardiovascularDisease')}</option>
            <option value="orthopedics">{t('orthopedics')}</option>
            <option value="vestibularRehabilitation">{t('vestibularRehabilitation')}</option>
            <option value="homecarePhysiotherapy">{t('homecarePhysiotherapy')}</option>
            <option value="pediatrics">{t('pediatrics')}</option>
            <option value="pelvicFloor">{t('pelvicFloor')}</option>
            <option value="womensHealthPhysiotherapy">{t('womensHealthPhysiotherapy')}</option>
            <option value="acupuncture">{t('acupuncture')}</option>
            <option value="magneticTherapy">{t('magneticTherapy')}</option>
            <option value="manualTherapy">{t('manualTherapy')}</option>
            <option value="oncology">{t('oncology')}</option>
            <option value="postOperativePhysiotherapist">{t('postOperativePhysiotherapist')}</option>
            <option value="rehabilitation">{t('rehabilitation')}</option>
            <option value="chestPhysiotherapy">{t('chestPhysiotherapy')}</option>
            <option value="womensHealth">{t('womensHealth')}</option>
          </select>
        </div>
        <div>
          <label>{t('yearsSinceLicensed')}</label>
          <input 
            type="number" 
            name="yearsSinceLicensed" 
            value={user.yearsSinceLicensed || ''} 
            onChange={handleChange}
            placeholder={t('enterYearOfLicense')}
            required
            min="1982"
            max="2024"
            readOnly
            title="This field cannot be changed"
          />
        </div>
        <br/>
        {changingPassword ? (
  <>
    <div>
      <label>{t('currentPassword')}</label>
      <input 
        type="password"
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
        required
      />
    </div>
    <div>
      <label>{t('newPassword')}</label>
      <input 
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
      />
    </div>
    <div>
      <label>{t('confirmPassword')}</label>
      <input 
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
      />
    </div>
    {passwordError && <span style={{ color: 'red' }}>{passwordError}</span>}
  </>
  ) : (
  <button
    type="button"
    onClick={() => setChangingPassword(true)}
  >
    {t('changePassword')}
  </button>
  )}
        <br/>
        <button type="submit">{t('saveProfile')}</button>
      </form>
    </div>
  );
}

export default UserProfile;